import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  Renderer2,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-top-scrollbar',
  templateUrl: './top-scrollbar.component.html',
  styleUrl: './top-scrollbar.component.scss',
})
export class TopScrollbarComponent implements OnDestroy, AfterViewInit, AfterViewChecked {
  scrollTarget = input<HTMLElement>();
  isRecordTable = input(false);
  isAgGrid = input(false);
  /** CM-27971: Pass the actual scrollable container directly for PrimeNG tables with updated DOM structure */
  directScrollElement = input<HTMLElement>();
  topScroll = viewChild<ElementRef>('topScroll');
  scrollContent = viewChild<ElementRef>('scrollContent');
  scrollEl: HTMLElement;
  tableBody!: HTMLElement;
  scrollHandler!: () => void;
  isAgGridTable = false;
  isMaterialTable = false;
  hasOverflow: boolean = false;
  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private pendingUpdate: number | null = null;
  private isInitialized = false;
  private readonly renderer = inject(Renderer2);
  private scrollTargetInitialized = false;
  constructor() {
    effect(() => {
      const target = this.scrollTarget();
      if (!this.scrollTargetInitialized) {
        this.scrollTargetInitialized = true;
        return;
      }
      if (target) {
        this.cleanup();
        this.scheduleInitialization();
      }
    });
  }
  ngAfterViewInit(): void {
    this.scheduleInitialization();
  }
  ngAfterViewChecked(): void {
    // Only initialize once after view is ready
    if (!this.isInitialized && this.scrollTarget() && this.topScroll()) {
      this.scheduleInitialization();
    }
  }
  private scheduleInitialization(): void {
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
    }
    this.pendingUpdate = requestAnimationFrame(() => {
      this.initialize();
      this.pendingUpdate = null;
    });
  }
  private initialize(): void {
    if (!this.scrollTarget() || !this.topScroll() || this.isInitialized) {
      return;
    }
    // Detect table type
    this.isAgGridTable = this.isAgGrid() || !!this.scrollTarget().querySelector('.ag-root-wrapper');
    // Material table can be the mat-table itself, or a wrapper containing mat-table
    this.isMaterialTable =
      this.scrollTarget().tagName === 'MAT-TABLE' ||
      !!this.scrollTarget().querySelector('mat-table') ||
      !!this.scrollTarget().querySelector('mat-header-row') ||
      this.scrollTarget().classList.contains('mat-table') ||
      this.scrollTarget().classList.contains('mat-table-wrapper');
    if (this.isAgGridTable) {
      this.initializeAgGrid();
    } else if (this.isMaterialTable) {
      this.initializeMaterialTable();
    } else {
      this.initializePrimeTable();
    }
    this.setupObservers();
    this.updateScrollbarWidth();
    this.checkOverflow();
    this.isInitialized = true;
  }
  private setupObservers(): void {
    if (!this.scrollTarget()) return;
    // ResizeObserver with requestAnimationFrame for performance
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.pendingUpdate) return;
        this.pendingUpdate = requestAnimationFrame(() => {
          this.updateScrollbarWidth();
          this.checkOverflow();
          this.pendingUpdate = null;
        });
      });
      if (this.isAgGridTable) {
        const agContainer = this.scrollTarget().querySelector('.ag-root-wrapper');
        if (agContainer) {
          this.resizeObserver.observe(agContainer);
        }
      } else if (this.isMaterialTable) {
        // Observe the mat-table element itself
        this.resizeObserver.observe(this.scrollTarget());
        // Also observe any mat-row elements for content changes
        const matRows = this.scrollTarget().querySelectorAll('mat-row, mat-header-row');
        matRows.forEach(row => this.resizeObserver.observe(row));
      } else if (this.tableBody) {
        const tableElement = this.tableBody.querySelector('table');
        const wrapperElement = this.tableBody.querySelector('.p-datatable-wrapper');
        // CM-27752 Observe the scrollable element for size changes
        if (this.scrollEl && this.scrollEl !== this.tableBody) {
          this.resizeObserver.observe(this.scrollEl);
        }
        if (tableElement) {
          this.resizeObserver.observe(tableElement);
        }
        if (wrapperElement) {
          this.resizeObserver.observe(wrapperElement);
        }
        // CM-27752 Observe tableBody itself as fallback
        this.resizeObserver.observe(this.tableBody);
      }
    }
    // MutationObserver with debounced requestAnimationFrame
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => {
        if (this.pendingUpdate) return;
        // Re-initialize tableBody reference if DOM structure changed
        if (!this.isAgGridTable && this.scrollTarget()) {
          const newTableBody = this.scrollTarget().children[0] as HTMLElement;
          if (newTableBody !== this.tableBody) {
            this.tableBody = newTableBody;
          }
        }
        this.pendingUpdate = requestAnimationFrame(() => {
          this.updateScrollbarWidth();
          this.checkOverflow();
          this.pendingUpdate = null;
        });
      });
      this.mutationObserver.observe(this.scrollTarget(), {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  }
  private initializePrimeTable(): void {
    if (!this.scrollTarget() || !this.topScroll()) return;
    this.tableBody = this.scrollTarget().children[0] as HTMLElement;
    // entity-subtab: use the provided scrollable container directly
    if (this.directScrollElement()) {
      this.scrollEl = this.directScrollElement();
    } else {
      this.scrollEl = this.isRecordTable() ? this.tableBody : (this.tableBody?.children[0] as HTMLElement);
    }
    if (this.scrollEl && this.topScroll()) {
      this.scrollHandler = () => this.syncScroll(this.scrollEl, this.topScroll().nativeElement);
      this.scrollEl.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }
  private initializeAgGrid(): void {
    if (!this.scrollTarget() || !this.topScroll()) return;
    // Find the horizontal scroll viewport
    this.scrollEl = this.scrollTarget().querySelector('.ag-body-horizontal-scroll-viewport') as HTMLElement;
    if (!this.scrollEl) {
      this.scrollEl = this.scrollTarget().querySelector('.ag-body-viewport') as HTMLElement;
    }
    if (this.scrollEl && this.topScroll()) {
      this.scrollHandler = () => this.syncScroll(this.scrollEl, this.topScroll().nativeElement);
      this.scrollEl.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }
  private initializeMaterialTable(): void {
    if (!this.scrollTarget() || !this.topScroll()) return;
    // For Material tables, check if scrollTarget is the wrapper or the mat-table itself
    // If it's a wrapper div, use it directly as the scroll element
    // If it's the mat-table, look for a parent wrapper
    if (this.scrollTarget().tagName === 'MAT-TABLE') {
      // scrollTarget is the mat-table, use it directly
      this.scrollEl = this.scrollTarget();
    } else {
      // scrollTarget is likely a wrapper div, use it as scroll element
      this.scrollEl = this.scrollTarget();
    }
    if (this.scrollEl && this.topScroll()) {
      this.scrollHandler = () => this.syncScroll(this.scrollEl, this.topScroll().nativeElement);
      this.scrollEl.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }
  updateScrollbarWidth(): void {
    if (!this.topScroll()?.nativeElement || !this.scrollContent()?.nativeElement) return;
    if (this.isAgGridTable) {
      this.updateAgGridScrollbarWidth();
    } else if (this.isMaterialTable) {
      this.updateMaterialTableScrollbarWidthWithRetry();
    } else {
      this.updatePrimeTableScrollbarWidthWithRetry();
    }
  }
  private updatePrimeTableScrollbarWidthWithRetry(retryCount = 0, maxRetries = 20): void {
    requestAnimationFrame(() => {
      // entity-subtab: use directScrollElement for exact measurements
      if (this.directScrollElement()) {
        const scrollWidth = this.directScrollElement().scrollWidth;
        const clientWidth = this.directScrollElement().clientWidth;
        if (scrollWidth === 0 || clientWidth === 0) {
          if (retryCount < maxRetries) {
            requestAnimationFrame(() => this.updatePrimeTableScrollbarWidthWithRetry(retryCount + 1, maxRetries));
          }
          return;
        }
        this.renderer.setStyle(this.scrollContent().nativeElement, 'width', `${scrollWidth}px`);
        this.renderer.setStyle(this.topScroll().nativeElement, 'width', `${clientWidth}px`);
        requestAnimationFrame(() => { this.checkPrimeTableOverflow(); });
        return;
      }
      // Original logic — untouched
      if (!this.tableBody) {
        if (retryCount < maxRetries) {
          setTimeout(() => this.updatePrimeTableScrollbarWidthWithRetry(retryCount + 1, maxRetries), 100);
        }
        return;
      }
      const tableElement = this.tableBody.querySelector('table');
      const wrapperElement = this.tableBody.querySelector('.p-datatable-wrapper') as HTMLElement;
      if (tableElement) {
        const scrollWidth = tableElement.scrollWidth;
        // CM-27752 Use actual scrollable element width, fallback to wrapper or tableBody clientWidth
        const clientWidth = wrapperElement 
          ? wrapperElement.clientWidth 
          : (this.scrollEl?.clientWidth || this.tableBody?.clientWidth || 0);
        // If dimensions are 0, table hasn't rendered yet - retry
        if (scrollWidth === 0 || clientWidth === 0) {
          if (retryCount < maxRetries) {
            requestAnimationFrame(() => this.updatePrimeTableScrollbarWidthWithRetry(retryCount + 1, maxRetries));
          }
          return;
        }
        const tableWidth = `${scrollWidth}px`;
        const wrapperWidth = `${clientWidth}px`;
        this.renderer.setStyle(this.scrollContent().nativeElement, 'width', tableWidth);
        this.renderer.setStyle(this.topScroll().nativeElement, 'width', wrapperWidth);
        // Force overflow check after styles are applied
        requestAnimationFrame(() => {
          this.checkPrimeTableOverflow();
        });
      } else if (retryCount < maxRetries) {
        // Elements not ready, retry
        requestAnimationFrame(() => this.updatePrimeTableScrollbarWidthWithRetry(retryCount + 1, maxRetries));
      }
    });
  }
  private updateMaterialTableScrollbarWidthWithRetry(retryCount = 0, maxRetries = 20): void {
    requestAnimationFrame(() => {
      if (!this.scrollTarget()) {
        if (retryCount < maxRetries) {
          requestAnimationFrame(() => this.updateMaterialTableScrollbarWidthWithRetry(retryCount + 1, maxRetries));
        }
        return;
      }
      const scrollWidth = this.scrollTarget().scrollWidth;
      const clientWidth = this.scrollTarget().clientWidth;
      // If dimensions are 0, table hasn't rendered yet - retry
      if (scrollWidth === 0 || clientWidth === 0) {
        if (retryCount < maxRetries) {
          requestAnimationFrame(() => this.updateMaterialTableScrollbarWidthWithRetry(retryCount + 1, maxRetries));
        }
        return;
      }
      const tableWidth = `${scrollWidth}px`;
      const wrapperWidth = `${clientWidth}px`;
      this.renderer.setStyle(this.scrollContent().nativeElement, 'width', tableWidth);
      this.renderer.setStyle(this.topScroll().nativeElement, 'width', wrapperWidth);
      // Force overflow check after styles are applied
      requestAnimationFrame(() => {
        this.checkMaterialTableOverflow();
      });
    });
  }
  private checkOverflow(): void {
    if (!this.topScroll()?.nativeElement) return;
    if (this.isAgGridTable) {
      this.checkAgGridOverflow();
    } else if (this.isMaterialTable) {
      this.checkMaterialTableOverflow();
    } else {
      this.checkPrimeTableOverflow();
    }
  }
  private checkPrimeTableOverflow(): void {
    // entity-subtab: use directScrollElement for exact overflow check
    if (this.directScrollElement()) {
      const scrollWidth = this.directScrollElement().scrollWidth;
      const clientWidth = this.directScrollElement().clientWidth;
      if (scrollWidth === 0 || clientWidth === 0) return;
      const hasHorizontalOverflow = scrollWidth > clientWidth;
      this.hasOverflow = hasHorizontalOverflow;
      const container = this.topScroll().nativeElement.parentElement;
      if (container) {
        this.renderer.setStyle(container, 'display', hasHorizontalOverflow ? 'block' : 'none');
      }
      return;
    }
    // Original logic — untouched
    if (!this.tableBody) return;
    const tableElement = this.tableBody.querySelector('table');
    const wrapperElement = this.tableBody.querySelector('.p-datatable-wrapper') as HTMLElement;
    if (tableElement && wrapperElement) {
      // Check if table content is wider than the visible wrapper area
      const tableScrollWidth = tableElement.scrollWidth;
      const wrapperClientWidth = wrapperElement.clientWidth;
      // Also check scrollEl if available (the actual scrollable element)
      const scrollElWidth = this.scrollEl ? this.scrollEl.scrollWidth : 0;
      const scrollElClientWidth = this.scrollEl ? this.scrollEl.clientWidth : 0;
      const scrollElOffsetWidth = this.scrollEl ? this.scrollEl.offsetWidth : 0;
      // Check if scrollEl actually has a scrollbar by checking computed overflow
      const scrollElHasNativeScrollbar = this.scrollEl
        ? this.scrollEl.scrollWidth > this.scrollEl.clientWidth ||
          (getComputedStyle(this.scrollEl).overflowX !== 'hidden' &&
            this.scrollEl.scrollWidth > this.scrollEl.offsetWidth)
        : false;
      // If dimensions are 0, table hasn't rendered yet - don't hide the scrollbar
      // Keep current state and wait for next update
      if (tableScrollWidth === 0 || wrapperClientWidth === 0) {
        return;
      }
      // Check both conditions: table overflow OR scrollEl overflow
      const hasTableOverflow = tableScrollWidth > wrapperClientWidth;
      const hasScrollElOverflow = scrollElWidth > scrollElClientWidth || scrollElHasNativeScrollbar;
      const hasHorizontalOverflow = hasTableOverflow || hasScrollElOverflow;
      this.hasOverflow = hasHorizontalOverflow;
      const container = this.topScroll().nativeElement.parentElement;
      if (container) {
        this.renderer.setStyle(container, 'display', hasHorizontalOverflow ? 'block' : 'none');
      }
    }
  }
  private checkAgGridOverflow(): void {
    if (!this.scrollTarget()) return;
    const agHorizontalScrollViewport = this.scrollTarget().querySelector(
      '.ag-body-horizontal-scroll-viewport'
    ) as HTMLElement;
    const agHorizontalScrollContainer = this.scrollTarget().querySelector(
      '.ag-body-horizontal-scroll-container'
    ) as HTMLElement;
    if (agHorizontalScrollViewport && agHorizontalScrollContainer) {
      const hasHorizontalOverflow = agHorizontalScrollContainer.scrollWidth > agHorizontalScrollViewport.clientWidth;
      this.hasOverflow = hasHorizontalOverflow;
      const container = this.topScroll().nativeElement.parentElement;
      if (container) {
        this.renderer.setStyle(container, 'display', hasHorizontalOverflow ? 'block' : 'none');
      }
    }
  }
  private checkMaterialTableOverflow(): void {
    if (!this.scrollTarget()) return;
    const scrollWidth = this.scrollTarget().scrollWidth;
    const clientWidth = this.scrollTarget().clientWidth;
    const offsetWidth = this.scrollTarget().offsetWidth;
    // If dimensions are 0, table hasn't rendered yet - keep current state
    if (scrollWidth === 0 || clientWidth === 0) {
      return;
    }
    // Check if table content is wider than visible area
    // Also check computed styles for overflow
    const computedStyle = getComputedStyle(this.scrollTarget());
    const hasNativeScrollbar =
      scrollWidth > clientWidth || (computedStyle.overflowX !== 'hidden' && scrollWidth > offsetWidth);
    const hasHorizontalOverflow = scrollWidth > clientWidth || hasNativeScrollbar;
    this.hasOverflow = hasHorizontalOverflow;
    const container = this.topScroll().nativeElement.parentElement;
    if (container) {
      this.renderer.setStyle(container, 'display', hasHorizontalOverflow ? 'block' : 'none');
    }
  }
  private updateAgGridScrollbarWidth(): void {
    if (!this.scrollTarget()) return;
    const agHorizontalScrollContainer = this.scrollTarget().querySelector(
      '.ag-body-horizontal-scroll-container'
    ) as HTMLElement;
    const agHorizontalScrollViewport = this.scrollTarget().querySelector(
      '.ag-body-horizontal-scroll-viewport'
    ) as HTMLElement;
    const agPinnedLeftContainer = this.scrollTarget().querySelector('.ag-pinned-left-cols-container') as HTMLElement;
    if (agHorizontalScrollViewport && agHorizontalScrollContainer) {
      const contentWidth = `${agHorizontalScrollContainer.scrollWidth}px`;
      const scrollbarWidth = `${agHorizontalScrollViewport.clientWidth}px`;
      const leftOffset = agPinnedLeftContainer ? `${agPinnedLeftContainer.offsetWidth}px` : '0px';
      this.renderer.setStyle(this.scrollContent().nativeElement, 'width', contentWidth);
      this.renderer.setStyle(this.topScroll().nativeElement, 'width', scrollbarWidth);
      this.renderer.setStyle(this.topScroll().nativeElement, 'margin-left', leftOffset);
    }
  }
  syncScroll(source: HTMLElement, target: HTMLElement): void {
    target.scrollLeft = source.scrollLeft;
  }
  private cleanup(): void {
    this.isInitialized = false;
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
      this.pendingUpdate = null;
    }
    if (this.scrollEl && this.scrollHandler) {
      this.scrollEl.removeEventListener('scroll', this.scrollHandler);
    }
    this.disconnectObservers();
  }
  private disconnectObservers(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
  /**
   * Public method for parent components to trigger a scrollbar refresh.
   * Useful when table content or structure changes externally.
   */
  public refreshScrollbar(): void {
    this.cleanup();
    this.scheduleInitialization();
  }
  ngOnDestroy(): void {
    this.cleanup();
  }
}
